import React from 'react';
import { connect } from 'react-redux';
import { compose, lifecycle, withStateHandlers, withHandlers } from 'recompose';
import { changeWidgetJSProp, updateWidgetJSPropField, recalculateArea } from '../../actions';
import _ from 'lodash';

import { IconButton, Toggle, FlatButton } from 'material-ui';
import IconFlip from 'material-ui/svg-icons/image/flip';
import ZoomInIcon from 'material-ui/svg-icons/action/zoom-in';
import ZoomOutIcon from 'material-ui/svg-icons/action/zoom-out';

import GradientOff from 'material-ui/svg-icons/image/blur-off';

import GradientLinear from 'material-ui/svg-icons/image/blur-linear';
import GradientCircular from 'material-ui/svg-icons/image/blur-circular';
import GradientCircularInv from 'material-ui/svg-icons/image/blur-on';

import { SketchPicker, SliderPicker, AlphaPicker } from 'react-color';

import color from 'color';

const isValid = area => {
    const { width, height, scale, x, y, cw, ch } = area;

    const realWidth = width * scale;
    const realHeight = height * scale;

    const reachX = realWidth + x;
    const reachY = realHeight + y;

    const coverLeft = x <= 0.1;
    const coverRight = reachX - cw >= -0.1;

    const coverTop = y <= 1;
    const coverBottom = reachY - ch >= -0.5;

    return coverLeft && coverRight && coverTop && coverBottom;
};

const calcFitPosition = area => {
    const { width, height, cw, ch } = area;
    const neededScaleX = cw / width;
    const neededScaleY = ch / height;

    const scale = +Math.max(neededScaleX, neededScaleY).toFixed(3);
    const x = (cw - width * scale).toFixed(3) / 2;
    const y = (ch - height * scale).toFixed(3) / 2;

    return {
        scale,
        x,
        y,
    };
};

export default compose(
    withStateHandlers(
        {
            propKey: null,
            colorPickerFor: null,
        },
        {
            enterEditMode: () => id => ({
                propKey: id,
            }),
            showColorPicker: () => colorPickerFor => ({
                colorPickerFor,
            }),
        },
    ),
    connect(
        ({ editor }, { propKey }) => ({
            im: _.get(editor, `widgetProps.${propKey}`, {}),
        }),
        {
            changeWidgetJSProp,
            updateWidgetJSPropField,
            recalculateArea,
        },
    ),
    withHandlers({
        handleScale: ({ im }) => delta => {
            const { scale = 1.0, width, height, x, y, cw, ch } = im;

            const defaults = calcFitPosition(im);
            const newScale = +Math.max(defaults.scale, +(scale + delta)).toFixed(3);
            const valid = isValid({ ...im, scale: newScale });

            const correlation = {};

            if (!valid) {
                // newScale
                const dx = x + newScale * width - cw;
                const dy = y + newScale * height - ch;

                if (dx < 0) correlation.x = +(x - dx).toFixed(3);
                if (dy < 0) correlation.y = +(y - dy).toFixed(3);
            }
            return {
                scale: newScale,
                ...correlation,
            };
        },
    }),

    lifecycle({
        onManipulation(e) {
            switch (e.detail.type) {
                case 'ACTIVATE': {
                    if (this.props.propKey) this.props.updateWidgetJSPropField(this.props.propKey, { active: false }); // reset active mode
                    this.props.enterEditMode(e.detail.id);
                    this.props.updateWidgetJSPropField(e.detail.id, { active: true });
                    break;
                }
                case 'CONTAINER_RESIZE': {
                    // this.props.updateWidgetJSPropField(e.detail.id, e.detail.values);

                    this.props.recalculateArea(e.detail.id, e.detail.values);

                    break;
                }
                case 'UPDATE': {
                    this.props.updateWidgetJSPropField(e.detail.id, e.detail.values);
                    break;
                }
                case 'CHANGE': {
                    const a = {};
                    a[e.detail.id] = e.detail.values;
                    this.props.changeWidgetJSProp(a);
                    break;
                }
            }
        },
        onClick(e) {},
        componentDidMount() {
            this._listenter = this.onManipulation.bind(this);
            this._clicklistenter = this.onClick.bind(this);
            window.addEventListener('WDP_IM', this._listenter, false);
            document.addEventListener('click', this._clicklistenter, false);
        },
        componentWillUnmount() {
            window.removeEventListener('WDP_IM', this._listenter);
            document.removeEventListener('click', this._clicklistenter);
        },
    }),
)(({ propKey, im, updateWidgetJSPropField, handleScale, showColorPicker, colorPickerFor }) => {
    const imageWidth = 250;

    if (!im)
        return (
            <div style={{ textAlign: 'center' }}>
                <i style={{ fontSize: 12 }}>Start image manipulation by adding custom background below </i>
            </div>
        );

    if (im && !im.url)
        return (
            <div style={{ textAlign: 'center' }}>
                <i style={{ fontSize: 12 }}>DROP AN IMAGE TO CONTAINER</i>
            </div>
        );
    const previewScale = imageWidth / im.width;

    let bgr = 'rgba(0,0,0,0.5)';

    if (im.oa == -1) bgr = `radial-gradient(${im.osc}, ${im.oec})`;
    else if (im.oa == -2) bgr = `radial-gradient( ${im.oec}, ${im.osc})`;
    else if (_.isInteger(im.oa)) bgr = `linear-gradient(${im.oa}deg, ${im.osc} 0%, ${im.oec} 100%)`;

    return (
        <div>
            <div style={{ textAlign: 'center', background: '#000', position: 'relative', width: imageWidth }}>
                {im && im.url && (
                    <img
                        alt=""
                        src={im.url}
                        style={{
                            objectFit: 'contain',
                            width: imageWidth,
                            transformOrigin: '0 0',
                            transform: `rotateX(${im.flipY ? 180 : 0}deg) rotateY(${
                                im.flipX ? 180 : 0
                            }deg) translateY(${im.flipY ? '-100%' : '0'}) translateX(${im.flipX ? '-100%' : '0'})`,
                        }}
                    />
                )}
                <div
                    style={{
                        position: 'absolute',
                        left: -((im.x * previewScale) / im.scale) + 'px',
                        top: -((im.y * previewScale) / im.scale) + 'px',
                        width: (im.cw * previewScale) / im.scale + 'px',
                        height: (im.ch * previewScale) / im.scale + 'px',
                        zIndex: 10,
                        boxShadow: '0 0 1px #fff',
                        background: bgr,
                    }}
                />
            </div>
            <IconButton
                onClick={() => {
                    const smartScale = handleScale(0.1);
                    updateWidgetJSPropField(propKey, { ...smartScale });
                }}
            >
                <ZoomInIcon />
            </IconButton>

            <IconButton
                tooltip="Flip Y"
                tooltipPosition="top-center"
                onClick={() => {
                    const smartScale = handleScale(-0.1);
                    updateWidgetJSPropField(propKey, { ...smartScale });
                }}
            >
                <ZoomOutIcon />
            </IconButton>

            <IconButton
                iconStyle={{ transform: `rotate(${im.flipX ? 0 : 180}deg` }}
                tooltip="Flip X"
                tooltipPosition="top-center"
                onClick={() => {
                    updateWidgetJSPropField(propKey, { flipX: !im.flipX });
                }}
            >
                <IconFlip />
            </IconButton>

            <IconButton
                tooltip="Flip Y"
                tooltipPosition="top-center"
                iconStyle={{ transform: `rotate(${im.flipY ? -90 : -270}deg` }}
                onClick={() => {
                    updateWidgetJSPropField(propKey, { flipY: !im.flipY });
                }}
            >
                <IconFlip />
            </IconButton>
            {/* <code>{JSON.stringify(im, null, 4)}</code> */}
            <div>Gradient</div>
            <div>
                {[
                    [null, GradientOff, 'off', {}, 'Off'],
                    [90, GradientLinear, 'linear90', { transform: `rotate(180deg)` }, 'to the right'],
                    [270, GradientLinear, 'linear270', { transform: `rotate(0deg)` }, 'to the left'],
                    [180, GradientLinear, 'linear0', { transform: `rotate(270deg)` }, 'from bottom'],
                    [0, GradientLinear, 'linear180', { transform: `rotate(90deg)` }, 'from top'],
                    [-1, GradientCircular, 'radial', {}, 'radial'],
                    [-2, GradientCircularInv, 'radialinv', {}, 'radial inverse'],
                ].map(([deg, Icon, key, style = {}, tooltip]) => (
                    <IconButton
                        key={key}
                        tooltip={tooltip}
                        tooltipPosition="top-center"
                        iconStyle={{ ...style }}
                        primary={deg == im.oa}
                        onClick={() => {
                            const update = {};
                            update.oa = deg;

                            if (!im.osc) update.osc = `rgba(0,0,0,0.1)`;
                            if (!im.oec) update.oec = `rgba(0,0,0,0.5)`;
                            updateWidgetJSPropField(propKey, update);
                        }}
                    >
                        <Icon color={deg == im.oa ? '#008c9d' : '#fff'} />
                    </IconButton>
                ))}
            </div>
            {im.oa !== null && (
                <div style={{ position: 'relative' }}>
                    <FlatButton
                        style={{ width: '50%' }}
                        icon={
                            <div
                                style={{
                                    width: 24,
                                    height: 24,
                                    boxShadow: '0 0 1px #fff',
                                    backgroundColor: im.osc || `rgba(0,0,0,0.1)`,
                                    display: 'inline-block',
                                }}
                            />
                        }
                        label="Start"
                        onClick={e => {
                            console.log(e.target.offsetTop);
                            showColorPicker('osc');
                        }}
                    />

                    <FlatButton
                        style={{ width: '50%' }}
                        icon={
                            <div
                                style={{
                                    width: 24,
                                    height: 24,
                                    boxShadow: '0 0 1px #fff',
                                    backgroundColor: im.oec || `rgba(0,0,0,0.5)`,
                                    display: 'inline-block',
                                }}
                            />
                        }
                        label="End"
                        onClick={e => {
                            console.log(e.target.offsetTop);
                            showColorPicker('oec');
                        }}
                    />

                    {colorPickerFor && (
                        <div
                            style={{
                                position: 'absolute',
                                zIndex: '2',
                            }}
                        >
                            <div
                                style={{
                                    position: 'fixed',
                                    top: '0px',
                                    right: '0px',
                                    bottom: '0px',
                                    left: '0px',

                                    background: '#000',
                                    opacity: 0.2,
                                }}
                                onClick={() => showColorPicker(null)}
                            />
                            <SketchPicker
                                key="pi"
                                color={im[colorPickerFor]}
                                onChangeComplete={({ rgb }) => {
                                    const update = {};

                                    update[colorPickerFor] = `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`;

                                    updateWidgetJSPropField(propKey, update);
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});



/** WEBPACK FOOTER **
 ** ../src/dash/front/editor/components/controls/IMControls.js
 **/